import { cloneDeep } from 'lodash'
import moment from 'moment'
import { toast } from 'react-toastify'

import {
  fetchBerthsOccupationFromBackend,
  fetchConfigFromBackend,
  fetchTerminalMaintenanceFromBackend,
  fetchTerminalVisitsFromBackend,
  insertTerminalMaintenanceInBackendRest,
  insertTerminalVisitInBackendRest,
  updateTerminalConfigInBackendRest,
  updateTerminalMaintenanceInBackendRest,
  updateTerminalVisitInBackendRest
} from '../../js/rest'
import { handleFetchError } from '../../js/sentry'
import { getTerminalVisitDataset, getTerminalVisitsDataset, sortTerminalVisits } from '../../utils/terminalPlannerUtils'

export function setTerminalConfig(config) {
  return {
    type: 'SET_TERMINAL_CONFIG',
    config
  }
}

function receiveTerminalVisitsSuccess(terminalVisits) {
  return {
    type: 'RECEIVE_TERMINAL_VISITS_SUCCESS',
    terminalVisits
  }
}

function requestTerminalVisits() {
  return {
    type: 'REQUEST_TERMINAL_VISITS'
  }
}

/**
 * On date change
 * @param {{from: string, to: string}} newDates
 * @param {{from: string, to: string}} prevDates
 * @param {string} terminalId
 * @param {Config} config
 * @param {string} authToken
 * @param {function} customFetch
 */
export function onDateChange(newDates, prevDates, terminalId, config, authToken, customFetch) {
  return async function (dispatch) {
    const from = moment(newDates.from).valueOf()
    const to = moment(newDates.to).valueOf()
    dispatch(setDateRange({ from, to }))

    // Check if the start time or end time goes out of the DATA range
    const isBeforeStartData = moment(newDates.from).isBefore(moment(prevDates.from))
    const isAfterEndData = moment(newDates.to).isAfter(moment(prevDates.to))

    // If date is outside data range, fetch new data
    if (isBeforeStartData || isAfterEndData) {
      dispatch(
        setDataRangeAndFetchData(
          terminalId,
          moment(cloneDeep(newDates.from)).startOf('day').subtract(1, 'weeks').valueOf(),
          moment(cloneDeep(newDates.to)).endOf('day').add(2, 'months').valueOf(),
          config,
          authToken,
          customFetch
        )
      )
    }
  }
}

export function setDateRange(value) {
  return {
    type: 'SET_SELECTED_DATE_RANGE',
    value
  }
}

export function setDataRange(value) {
  return {
    type: 'SET_SELECTED_DATA_RANGE',
    value
  }
}

/**
 * @param {TerminalVisit?} selectedVisit
 * @param {string?} selectedOrderId
 */
export function setSelectedVisit(selectedVisit, selectedOrderId) {
  return {
    type: 'SET_SELECTED_VISIT',
    selectedVisit,
    selectedOrderId
  }
}

export function setSelectedMaintenance(value) {
  return {
    type: 'SET_SELECTED_MAINTENANCE',
    value
  }
}

export function setTerminalVisits(value) {
  return {
    type: 'SET_TERMINAL_VISITS',
    value
  }
}

/**
 * Update a terminal visit or add a new terminal visit to the list with visits
 * @param {TerminalVisit} terminalVisit
 * @returns {{type: string, terminalVisit: TerminalVisit}}
 */
export function updateTerminalVisit(terminalVisit) {
  return {
    type: 'UPDATE_TERMINAL_VISIT',
    terminalVisit
  }
}

export function changeTerminalVisits(terminalVisitChanges) {
  return {
    type: 'CHANGE_TERMINAL_VISITS',
    terminalVisitChanges
  }
}

export function setActiveOverlays(overlays) {
  return {
    type: 'SET_ACTIVE_OVERLAYS',
    overlays
  }
}

export function setConflictModalDimensionsInStore(dimensions) {
  return {
    type: 'SET_CONFLICT_MODAL_DIMENSIONS',
    dimensions
  }
}

export function setConflictModalConflictsInStore(conflicts) {
  return {
    type: 'SET_CONFLICT_MODAL_CONFLICTS',
    conflicts
  }
}
function receiveBerthsOccupationSuccess(berthsOccupation) {
  return {
    type: 'RECEIVE_BERTHS_OCCUPATION_SUCCESS',
    berthsOccupation
  }
}

function requestBerthsOccupation() {
  return {
    type: 'REQUEST_BERTHS_OCCUPATION'
  }
}

export function setWebSocketError(webSocketError) {
  return {
    type: 'SET_WEBSOCKET_ERROR',
    webSocketError
  }
}

export function clearWebSocketError() {
  return {
    type: 'CLEAR_WEBSOCKET_ERROR'
  }
}

export function updateTerminalConfig(config) {
  return {
    type: 'UPDATE_TERMINAL_CONFIG',
    config
  }
}

function requestTerminalMaintenance() {
  return {
    type: 'REQUEST_TERMINAL_MAINTENANCE'
  }
}

function receiveTerminalMaintenanceSuccess(terminalMaintenance) {
  return {
    type: 'RECEIVE_TERMINAL_MAINTENANCE_SUCCESS',
    terminalMaintenance
  }
}

function updateTerminalMaintenance(terminalMaintenance) {
  return {
    type: 'UPDATE_TERMINAL_MAINTENANCE',
    terminalMaintenance
  }
}

export function setTerminalMaintenance(value) {
  return {
    type: 'SET_TERMINAL_MAINTENANCE',
    value
  }
}

/**
 * Fetch berths occupancy from backend and set them in redux store
 * @param terminalId
 * @param from
 * @param to
 * @param authToken
 * @param customFetch
 * @returns {Function}
 */
export function fetchConfigAndTerminalContent(terminalId, from, to, authToken, customFetch, callbackFinishLoading) {
  return async function (dispatch) {
    try {
      const config = await fetchConfigFromBackend(terminalId, authToken, customFetch)
      dispatch(setTerminalConfig(config))

      dispatch(fetchTerminalVisits(terminalId, from, to, config, authToken, customFetch))
      dispatch(fetchTerminalMaintenance(terminalId, from, to, authToken, customFetch))
      if (config && config.quays && config.quays[0].berths) {
        dispatch(fetchBerthsOccupation(terminalId, from, to, authToken, customFetch))
      }
    } catch (err) {
      handleFetchError(err)
    } finally {
      callbackFinishLoading()
    }
  }
}

/**
 * Set data range and fetch data
 * @param {string} terminalId
 * @param {number} from
 * @param {number} to
 * @param {Config} config
 * @param {string} authToken
 * @param {function} customFetch
 * @returns {Function}
 */
export function setDataRangeAndFetchData(terminalId, from, to, config, authToken, customFetch) {
  return async function (dispatch) {
    try {
      dispatch(setDataRange({ from, to }))
      dispatch(fetchTerminalVisits(terminalId, from, to, config, authToken, customFetch))
      dispatch(fetchTerminalMaintenance(terminalId, from, to, authToken, customFetch))
      dispatch(fetchBerthsOccupation(terminalId, from, to, authToken, customFetch))
    } catch (err) {
      handleFetchError(err)
    }
  }
}

/**
 * Fetch terminalVisits from backend and set them in redux store
 * OVERWRITES OLD TERMINALVISITS IN LOCAL STORE!
 */
export function fetchTerminalVisits(terminalId, from, to, config, authToken, customFetch) {
  return async function (dispatch) {
    try {
      dispatch(requestTerminalVisits())
      const fetchedTerminalVisits = await fetchTerminalVisitsFromBackend(terminalId, from, to, authToken, customFetch)

      const sortedTerminalVisits = sortTerminalVisits(fetchedTerminalVisits)

      // Transform the terminal visits with additional data
      const terminalVisits = getTerminalVisitsDataset(sortedTerminalVisits, config)

      dispatch(receiveTerminalVisitsSuccess(terminalVisits))
    } catch (err) {
      handleFetchError(err)
    }
  }
}

/**
 * Updates terminal visit
 * @param terminalVisitToUpdate
 * @param authToken
 * @param customFetch
 * @returns {Function}
 */
export function updateTerminalVisitInBackend(terminalVisitToUpdate, authToken, customFetch) {
  return async function (dispatch) {
    try {
      dispatch(updateTerminalVisit(terminalVisitToUpdate))

      // Update the visit in the backend AFTER we update the visit locally, we do this so we dont have to
      // wait for the response from the backend -> creates a much smoother experience
      // todo: think about if this is what we want, I can also imagine we would like to show a loader over the visit while it is being saved in the backend
      await updateTerminalVisitInBackendRest(terminalVisitToUpdate, authToken, customFetch)
    } catch (err) {
      handleFetchError(err)
    }
  }
}

/**
 * Adds new terminalVisit
 * @param {TerminalVisit} terminalVisit
 * @param {Config} config
 * @param {string} authToken
 * @param {function} customFetch
 * @returns {Function}
 */
export function insertTerminalVisitInBackend(terminalVisit, config, authToken, customFetch) {
  return async function (dispatch) {
    try {
      const insertedTerminalVisit = await insertTerminalVisitInBackendRest(terminalVisit, authToken, customFetch)

      // Transform the terminal visits with additional data
      const terminalVisitUpdated = getTerminalVisitDataset(insertedTerminalVisit, config)

      dispatch(updateTerminalVisit(terminalVisitUpdated))
    } catch (err) {
      handleFetchError(err)
    }
  }
}

/**
 * Fetch berths occupancy from backend and set them in redux store
 * @param terminalId
 * @param from
 * @param to
 * @param authToken
 * @param customFetch
 * @returns {Function}
 */
export function fetchBerthsOccupation(terminalId, from, to, authToken, customFetch) {
  return async function (dispatch) {
    try {
      dispatch(requestBerthsOccupation())
      const berthsOccupationList = await fetchBerthsOccupationFromBackend(
        terminalId,
        from,
        to,
        authToken,
        customFetch,
        'DAY'
      )
      dispatch(receiveBerthsOccupationSuccess(berthsOccupationList))
    } catch (err) {
      handleFetchError(err)
    }
  }
}

/**
 * Updates terminal config
 * @param config
 * @param authToken
 * @param customFetch
 * @returns {Function}
 */
export function updateTerminalConfigInBackend(config, authToken, customFetch) {
  return async function (dispatch) {
    try {
      const newConfig = await updateTerminalConfigInBackendRest(config, authToken, customFetch)
      dispatch(updateTerminalConfig(newConfig))
      toast.success('Saved terminal configurarion')
    } catch (err) {
      handleFetchError(err)
    }
  }
}

/**
 * Fetch terminalMaintenance from backend and set them in redux store
 * OVERWRITES OLD TERMINALMAINTENANCE IN LOCAL STORE!
 * @param terminalId
 * @param from
 * @param to
 * @param authToken
 * @param customFetch
 * @returns {Function}
 */
export function fetchTerminalMaintenance(terminalId, from, to, authToken, customFetch) {
  return async function (dispatch) {
    try {
      dispatch(requestTerminalMaintenance())
      const terminalMaintenanceList = await fetchTerminalMaintenanceFromBackend(
        terminalId,
        from,
        to,
        authToken,
        customFetch
      )
      dispatch(receiveTerminalMaintenanceSuccess(terminalMaintenanceList))
    } catch (err) {
      handleFetchError(err)
    }
  }
}

/**
 * Adds new terminalMaintenance
 * @param terminalMaintenance
 * @param authToken
 * @param customFetch
 * @returns {Function}
 */
export function insertTerminalMaintenanceInBackend(
  terminalMaintenance,
  allTerminalMaintenance,
  authToken,
  customFetch
) {
  return async function (dispatch) {
    try {
      const insertedTerminalMaintenance = await insertTerminalMaintenanceInBackendRest(
        terminalMaintenance,
        authToken,
        customFetch
      )
      allTerminalMaintenance.push(insertedTerminalMaintenance)
      dispatch(updateTerminalMaintenance(allTerminalMaintenance))
    } catch (err) {
      handleFetchError(err)
    }
  }
}

/**
 * Updates terminal maintenance
 * @param terminalMaintenanceToUpdate
 * @param terminalMaintenanceList
 * @param authToken
 * @param customFetch
 * @returns {Function}
 */
export function updateTerminalMaintenanceInBackend(
  terminalMaintenanceToUpdate,
  terminalMaintenanceList,
  authToken,
  customFetch
) {
  return async function (dispatch) {
    try {
      // Filter out old terminalMaintenance from local visits list and add the updated terminalMaintenance that
      // comes in the response from the server
      const filteredTerminalMaintenanceList = terminalMaintenanceList.filter(
        terminalMaintenance => terminalMaintenance._id !== terminalMaintenanceToUpdate._id
      )
      filteredTerminalMaintenanceList.push(terminalMaintenanceToUpdate)

      dispatch(updateTerminalMaintenance(filteredTerminalMaintenanceList))

      // Update the maintenance in the backend AFTER we update the maintenance locally, we do this so we dont have to
      // wait for the response from the backend -> creates a much smoother experience
      // todo: think about if this is what we want, I can also imagine we would like to show a loader over the maintenance while it is being saved in the backend
      await updateTerminalMaintenanceInBackendRest(terminalMaintenanceToUpdate, authToken, customFetch)
    } catch (err) {
      handleFetchError(err)
    }
  }
}
